// ENDINGS for playback: how the last pass finishes.
//
// A rhythm section has three stock ways of closing a tune that nobody writes
// on the chart — they are called on the stand: RITARDANDO (pull the last bars
// back), the TAG (repeat the last two or four bars, twice or three times, the
// "tag the turnaround" ending) and the HELD LAST CHORD (the final bar as a
// fermata, the band landing together and ringing out). This module is the
// pure half: the setting's shape, where the final bars sit in a played form,
// and the ritardando's tempo curve. The playback-form builder writes a tag out
// (sequencePlayback.ts), the band hook turns the held bar into a hold window
// (holds.ts), and the player stretches the beats inside the ritardando.

import type { IRealMeasure } from '@/music/ireal/types';
import { measureStartBeats } from '@/music/chart';
import type { HoldWindow } from './holds';

export type RitardandoBars = 0 | 1 | 2 | 4;
export type TagBars = 0 | 2 | 4;
export type TagTimes = 2 | 3;
/**
 * Where the ending LANDS (#429). Many charts end on a turnaround that leads
 * back to the top, so the last pass stops unresolved. `home` adds a bar on the
 * key chord when the last bar is not already there; `flat-six` and `flat-two`
 * reach it through ♭VI∆ or ♭II∆ first; `top` stops on the downbeat of the
 * next chorus, the first chord of bar 1.
 */
export type ResolveEnding = 'off' | 'home' | 'flat-six' | 'flat-two' | 'top';

export interface EndingOptions {
  /** Slow down over the last N bars (0 = play the ending in time). */
  ritardandoBars: RitardandoBars;
  /** Repeat the last N bars as a tag (0 = no tag). */
  tagBars: TagBars;
  /** How many times the tag plays in all, the written pass included. */
  tagTimes: TagTimes;
  /** Hold the final bar: one strike, time stretched, the band ringing out. */
  holdLast: boolean;
  /** Add a resolving bar after an unresolved last bar (playback only). */
  resolve: ResolveEnding;
}

export const DEFAULT_ENDING: EndingOptions = {
  ritardandoBars: 0,
  tagBars: 0,
  tagTimes: 3,
  holdLast: false,
  resolve: 'off',
};

export const RITARDANDO_BARS: RitardandoBars[] = [0, 1, 2, 4];
export const TAG_BARS: TagBars[] = [0, 2, 4];
export const TAG_TIMES: TagTimes[] = [2, 3];
export const RESOLVE_ENDINGS: ResolveEnding[] = ['off', 'home', 'flat-six', 'flat-two', 'top'];

/** Whether any ending device is switched on. */
export function endingActive(ending: EndingOptions | undefined): boolean {
  return (
    !!ending &&
    (ending.ritardandoBars > 0 || ending.tagBars > 0 || ending.holdLast || ending.resolve !== 'off')
  );
}

/** A persisted blob is untrusted: every field falls back to its default. */
export function normalizeEnding(input: unknown): EndingOptions {
  const raw = (input ?? {}) as Partial<Record<keyof EndingOptions, unknown>>;
  const pick = <T>(value: unknown, allowed: readonly T[], fallback: T): T =>
    (allowed as readonly unknown[]).includes(value) ? (value as T) : fallback;
  return {
    ritardandoBars: pick(raw.ritardandoBars, RITARDANDO_BARS, DEFAULT_ENDING.ritardandoBars),
    tagBars: pick(raw.tagBars, TAG_BARS, DEFAULT_ENDING.tagBars),
    tagTimes: pick(raw.tagTimes, TAG_TIMES, DEFAULT_ENDING.tagTimes),
    holdLast: typeof raw.holdLast === 'boolean' ? raw.holdLast : DEFAULT_ENDING.holdLast,
    resolve: pick(raw.resolve, RESOLVE_ENDINGS, DEFAULT_ENDING.resolve),
  };
}

/**
 * The beat window of the LAST `bars` measures of a played form (start
 * inclusive, end = the form's end). Meter-aware and pickup-aware, like every
 * other window the player reads. Undefined for no bars or an empty form; a
 * form shorter than the ask gives its whole length.
 */
export function lastBarsWindow(
  measures: Pick<IRealMeasure, 'timeSig' | 'pickupBeats'>[],
  defaultTimeSig: [number, number],
  bars: number,
): HoldWindow | undefined {
  if (bars <= 0 || measures.length === 0) return undefined;
  const starts = measureStartBeats(measures, defaultTimeSig);
  let meter = defaultTimeSig;
  for (const m of measures) meter = m.timeSig ?? meter;
  const last = measures[measures.length - 1];
  const end = starts[starts.length - 1] + (last.pickupBeats ?? meter[0]);
  const from = Math.max(0, measures.length - Math.round(bars));
  return { start: starts[from], end };
}

/** How much slower the ritardando's LAST beat plays: 1.6× the beat = ~62% of the tempo. */
export const RITARDANDO_DEPTH = 0.6;

/**
 * The stretch factor for a beat inside a ritardando window: 1 outside it, then
 * a straight line from just above 1 on the window's first beat to
 * `1 + RITARDANDO_DEPTH` on its last — the linear pull-back a band plays,
 * every beat a little longer than the one before. The FIRST beat of the
 * window already stretches a little (progress is counted at the beat's END),
 * so the slow-down is audible from the bar it starts on.
 */
export function ritardandoStretch(window: HoldWindow | undefined, beat: number): number {
  if (!window) return 1;
  const length = window.end - window.start;
  if (!(length > 0) || beat < window.start - 1e-3 || beat >= window.end - 1e-3) return 1;
  const progress = Math.min(1, Math.max(0, (beat - window.start + 1) / length));
  return 1 + RITARDANDO_DEPTH * progress;
}
